import { isImageOrVideoObject } from "../layerUtils";

/**
 * 画布图片处理器
 * 负责将图片转换为 WebP 格式以优化加载性能
 *
 * 【功能说明】
 * 1. 将图片 URL 转换为 WebP 格式（质量85）
 * 2. 保持原始图片尺寸（不缩放），仅按需降采样（分享页只读场景）
 * 3. 保留原始链接到元素的 originalSrc 属性中
 * 4. 兼容旧数据（已包含处理参数的图片不做处理）
 */

/**
 * WebP 转换配置
 */
export const WEBP_CONFIG = {
  /** 图片质量 0-100 */
  QUALITY: 80,
  /** 输出格式 */
  FORMAT: "webp" as const,
  /** 是否保持原始尺寸 */
  KEEP_ORIGINAL_SIZE: true
};

/**
 * 支持 OSS resize 降采样的域名白名单
 * 超大图（如 6080×8192）在移动端按原始分辨率解码会导致 WebView 内存崩溃自动刷新，
 * 因此只对这些 OSS 域名追加 resize 参数做降采样；其他外部图床不处理，避免参数无效。
 */
const DOWNSCALE_OSS_DOMAINS = ["genai.holopix.cn"];

/**
 * 判断 URL 是否已经包含处理参数
 * @param url 图片 URL
 * @returns 是否已处理
 */
export const hasProcessingParams = (url?: string): boolean => {
  if (!url) return false;
  return url.includes("x-oss-process=");
};

/**
 * 生成 WebP 处理参数
 * 【策略】保持原始尺寸，仅转换格式和质量
 *
 * OSS 图片处理参数说明：
 * - format,webp: 转换为 WebP 格式
 * - quality,q_80: 设置质量为 80
 * - resize: 不包含 resize 参数，保持原始尺寸
 *
 * @returns OSS 处理参数字符串
 */
const generateWebPParams = (): string => {
  const { QUALITY, FORMAT } = WEBP_CONFIG;
  return `image/format,${FORMAT}/quality,q_${QUALITY}`;
};

/**
 * 将图片 URL 转换为 WebP 格式
 * 【核心逻辑】
 * 1. 已包含处理参数：原样返回（避免重复处理）
 * 2. 其他图片：追加 WebP 转换参数
 *
 * @param originalUrl 原始图片 URL
 * @returns 处理后的 URL（WebP 格式）
 */
export const convertToWebP = (originalUrl?: string): string | undefined => {
  if (!originalUrl) return originalUrl;

  // 已包含处理参数的图片不再处理
  if (hasProcessingParams(originalUrl)) {
    return originalUrl;
  }

  // 生成 WebP 处理参数
  const webpParams = generateWebPParams();

  // 拼接处理参数
  return `${originalUrl}?x-oss-process=${webpParams}`;
};

/**
 * 对 OSS 图片 URL 追加 resize 降采样参数
 *
 * 【背景】分享画板可能包含超大分辨率图片（如 6080×8192，解码位图约 200MB/张），
 * 移动端 WebView 按原始分辨率解码时会内存崩溃并被系统自动重载，表现为"画布渲染中不停刷新"。
 * 分享页为只读展示，按屏幕所需尺寸降采样（originalSrc 仍保留原始高清图，不影响复制/下载）。
 *
 * @param url     图片 URL（可含 x-oss-process 参数）
 * @param maxEdge 限制后的最大边长（<=0 或未传时不处理）
 */
export const applyImageDownscale = (url?: string, maxEdge?: number): string | undefined => {
  if (!url || !maxEdge || maxEdge <= 0) return url;

  try {
    const host = new URL(url).hostname;
    const isAllowedDomain = DOWNSCALE_OSS_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
    if (!isAllowedDomain) return url;
  } catch {
    return url;
  }

  const parsed = parseOssParams(url);
  if (!parsed) return url;

  const { baseUrl, params } = parsed;
  const parts = params.split("/");
  const resizeParam = `resize,m_lfit,limit_0,w_${maxEdge}`;
  const resizeIndex = parts.findIndex((part) => part.startsWith("resize,"));
  if (resizeIndex !== -1) {
    parts[resizeIndex] = resizeParam;
  } else {
    parts.push(resizeParam);
  }
  return `${baseUrl}?x-oss-process=${parts.join("/")}`;
};

/**
 * 处理图片对象数据
 * 【关键逻辑】
 * 1. 将 src 转换为 WebP 格式用于渲染
 * 2. 将原始链接保存到 originalSrc 属性（用于后续高清下载、重绘等）
 * 3. 如果已有 originalSrc，则保留原有的 originalSrc
 *
 * @param imageObj 图片对象数据
 * @returns 处理后的图片对象数据
 */
export const processImageObject = (imageObj: any, maxEdge?: number): any => {
  if (!imageObj || imageObj.type !== "image" || !imageObj.src) {
    return imageObj;
  }

  const originalSrc = imageObj.src;

  // 如果已经处理过（已有 originalSrc 且 src 包含处理参数），则跳过
  if (imageObj.originalSrc && hasProcessingParams(imageObj.src)) {
    return imageObj;
  }

  // 转换为 WebP，并按需降采样（maxEdge 仅在分享页只读场景传入）
  const webpUrl = convertToWebP(originalSrc);
  const optimizedUrl = applyImageDownscale(webpUrl, maxEdge) || webpUrl;

  // 如果转换后的 URL 与原始 URL 不同，说明已处理
  if (optimizedUrl !== originalSrc) {
    return {
      ...imageObj,
      src: optimizedUrl,
      originalSrc: imageObj.originalSrc || originalSrc // 保留已有的 originalSrc，否则使用当前 src
    };
  }

  // 已包含处理参数或非图片 URL，原样返回
  return imageObj;
};

/**
 * 批量处理画布对象列表中的图片
 * 递归处理 group 内的子对象，确保嵌套图片也能被优化
 * @param objects 画布对象列表
 * @param maxEdge 可选：图片降采样最大边（仅分享页只读场景传入）
 * @returns 处理后的对象列表
 */
export const processCanvasObjects = (objects: any[], maxEdge?: number): any[] => {
  if (!Array.isArray(objects)) return objects;

  return objects.map((obj) => {
    if (isImageOrVideoObject(obj)) {
      return processImageObject(obj, maxEdge);
    }
    if (obj.type === "group" && Array.isArray(obj.objects)) {
      return {
        ...obj,
        objects: processCanvasObjects(obj.objects, maxEdge)
      };
    }
    return obj;
  });
};

/**
 * 获取图片的原始高清链接
 * 用于下载、重绘、图生图等需要高清原图的场景
 *
 * @param imageObj 图片对象
 * @returns 原始高清链接
 */
export const getOriginalImageUrl = (imageObj: any): string | undefined => {
  if (!imageObj || imageObj.type !== "image") return undefined;

  // 优先使用 originalSrc（保存的原始链接）
  if (imageObj.originalSrc) {
    return imageObj.originalSrc;
  }

  // 如果没有 originalSrc，则使用 src（可能是已处理的 WebP 链接）
  return imageObj.src;
};

/**
 * 判断图片是否需要 WebP 转换
 * @param url 图片 URL
 * @returns 是否需要转换
 */
export const needsWebPConversion = (url?: string): boolean => {
  if (!url) return false;

  // 已包含处理参数的不需要转换
  if (hasProcessingParams(url)) return false;

  return true;
};

/**
 * 解析 OSS 图片处理参数
 * @param url 图片 URL
 * @returns 解析后的参数对象，如果没有参数返回 null
 */
const parseOssParams = (url: string): { baseUrl: string; params: string } | null => {
  const match = url.match(/^(.*?)\?x-oss-process=(.*)$/);
  if (!match) return null;
  return { baseUrl: match[1], params: match[2] };
};

/**
 * 处理白名单域名的图片 URL
 * 【逻辑】
 * 1. 如果 URL 没有阿里云参数，添加 WebP 转换参数
 * 2. 如果 URL 有阿里云参数：
 *    - 移除 resize 相关的参数（长度/宽度）
 *    - 确保格式转换为 webp
 *
 * @param url 原始图片 URL
 * @returns 处理后的 URL
 */
export const processAllowedDomainUrl = (url?: string): string | undefined => {
  if (!url) return url;

  const parsed = parseOssParams(url);

  // 没有阿里云参数，直接添加 WebP 参数
  if (!parsed) {
    return convertToWebP(url);
  }

  const { baseUrl, params } = parsed;
  const paramParts = params.split("/");

  // 过滤掉 resize 参数（长度/宽度），保留其他参数
  const filteredParts = paramParts.filter((part) => {
    // 移除 resize 相关的参数
    if (part.startsWith("resize,")) return false;
    return true;
  });

  // 检查是否已有 format 参数
  const hasFormat = filteredParts.some((part) => part.startsWith("format,"));

  // 如果没有 format 参数，添加 webp 格式
  if (!hasFormat) {
    filteredParts.push(`format,${WEBP_CONFIG.FORMAT}`);
  } else {
    // 如果有 format 参数，确保是 webp
    const formatIndex = filteredParts.findIndex((part) => part.startsWith("format,"));
    if (formatIndex !== -1) {
      filteredParts[formatIndex] = `format,${WEBP_CONFIG.FORMAT}`;
    }
  }

  // 如果没有 quality 参数，添加质量参数
  const hasQuality = filteredParts.some((part) => part.startsWith("quality,"));
  if (!hasQuality) {
    filteredParts.push(`quality,q_${WEBP_CONFIG.QUALITY}`);
  }

  // 重新构建 URL
  return `${baseUrl}?x-oss-process=${filteredParts.join("/")}`;
};

/**
 * 从带处理参数的 URL 中提取原始 URL
 * 【用途】当调用方未传入 originalSrc 时，从 WebP URL 还原原始地址
 *
 * @param url 可能包含 OSS 处理参数的图片 URL
 * @returns 去除处理参数后的原始 URL
 */
export const extractOriginalUrl = (url?: string): string | undefined => {
  if (!url) return url;

  // 去除 ?x-oss-process=... 及其后续内容
  const index = url.indexOf("?x-oss-process=");
  if (index !== -1) {
    return url.substring(0, index);
  }

  // 如果没有处理参数，原样返回
  return url;
};
